import { supabase } from "@/lib/supabase";
import type {
  AdvisorMovement,
  AppData,
  AppNotification,
  ApprovalStatus,
  Client,
  Distributor,
  Encargo,
  EncargoDeliverMode,
  EncargoStatus,
  Expense,
  ImportLog,
  InventoryAdjustment,
  InventoryItem,
  Loan,
  Product,
  Promotion,
  Receivable,
  RestockItem,
  Role,
  Sale,
  Settings,
  StockEntry,
  TeamMember,
} from "@/types";
import {
  advisorMovementToRow,
  clientToRow,
  distributorToRow,
  encargoToRow,
  expenseToRow,
  importLogToRow,
  inventoryAdjustmentToRow,
  inventoryToRow,
  loanToRow,
  notificationToRow,
  productToRow,
  promotionToRow,
  restockItemToRow,
  receivableToRow,
  rowToAdvisorMovement,
  rowToClient,
  rowToDistributor,
  rowToEncargo,
  rowToExpense,
  rowToImportLog,
  rowToInventory,
  rowToInventoryAdjustment,
  rowToLoan,
  rowToNotification,
  rowToProduct,
  rowToPromotion,
  rowToRestockItem,
  rowToReceivable,
  rowToSale,
  rowToSettings,
  rowToStockEntry,
  rowToTeamMember,
  isNewId,
  normalizeSale,
  saleToRow,
  stockEntryToRow,
} from "@/store/mappers";

type Row = Record<string, unknown>;

const unwrap = <T>(res: { data: T; error: { message: string } | null }): T => {
  if (res.error) throw new Error(res.error.message);
  return res.data;
};

const unwrapList = (res: { data: unknown; error: { message: string } | null }): Row[] => {
  if (res.error) throw new Error(res.error.message);
  return (res.data as Row[]) ?? [];
};

// Marca como "admin" las ventas que no pertenecen a un asesor conocido
// (ventas directas de Juanka), conservando el uuid de las ventas de asesores.
const normalizeSales = (advisorIds: Set<string>, sales: Sale[]): Sale[] =>
  sales.map((s) => normalizeSale(s, advisorIds));

export type LoadedData = Omit<AppData, "period" | "role">;

export const loadAll = async (): Promise<LoadedData> => {
  const [
    products,
    profiles,
    clients,
    sales,
    inventory,
    advisorMovements,
    expenses,
    receivables,
    loans,
    promotions,
    notifications,
    settings,
    importLogs,
    encargos,
    restockItems,
    stockEntries,
    inventoryAdjustments,
  ] = await Promise.all([
    supabase.from("products").select("*").order("name", { ascending: true }),
    supabase.from("profiles").select("*").order("names", { ascending: true }),
    supabase.from("clients").select("*").order("name", { ascending: true }),
    supabase.from("sales").select("*").order("date", { ascending: false }),
    supabase.from("inventory_items").select("*"),
    supabase.from("advisor_movements").select("*").order("date", { ascending: false }),
    supabase.from("expenses").select("*").order("date", { ascending: false }),
    supabase.from("receivables").select("*").order("date", { ascending: false }),
    supabase.from("loans").select("*").order("date", { ascending: false }),
    supabase.from("promotions").select("*").order("created_at", { ascending: false }),
    supabase.from("notifications").select("*").order("date", { ascending: false }),
    supabase.from("app_settings").select("*").eq("id", 1).maybeSingle(),
    supabase.from("import_logs").select("*").order("created_at", { ascending: false }).limit(20),
    supabase.from("encargos").select("*").order("created_at", { ascending: false }),
    supabase.from("restock_items").select("*").order("created_at", { ascending: false }),
    supabase.from("stock_entries").select("*").order("date", { ascending: false }),
    supabase.from("inventory_adjustments").select("*").order("date", { ascending: false }),
  ]);

  const distributors = unwrapList(profiles)
    .map(rowToDistributor)
    .filter((d) => d.id && d.role === "asesor");
  const advisorIds = new Set(distributors.map((d) => d.id));

  return {
    products: unwrapList(products).map(rowToProduct),
    distributors,
    clients: unwrapList(clients).map(rowToClient),
    sales: normalizeSales(advisorIds, unwrapList(sales).map(rowToSale)),
    inventory: unwrapList(inventory).map(rowToInventory),
    advisorMovements: unwrapList(advisorMovements).map(rowToAdvisorMovement),
    expenses: unwrapList(expenses).map(rowToExpense),
    receivables: unwrapList(receivables).map(rowToReceivable),
    loans: unwrapList(loans).map(rowToLoan),
    promotions: unwrapList(promotions).map(rowToPromotion),
    notifications: unwrapList(notifications).map(rowToNotification),
    encargos: unwrapList(encargos).map(rowToEncargo),
    restockItems: unwrapList(restockItems).map(rowToRestockItem),
    settings: rowToSettings((settings.data as Row) ?? null),
    importLogs: unwrapList(importLogs).map(rowToImportLog),
    stockEntries: unwrapList(stockEntries).map(rowToStockEntry),
    inventoryAdjustments: unwrapList(inventoryAdjustments).map(rowToInventoryAdjustment),
  };
};

// ------------------------------------------------------------------ Product
export const saveProduct = async (p: Product): Promise<Product> => {
  const isNew = isNewId(p.id);
  const query = isNew
    ? supabase.from("products").insert(productToRow(p)).select().single()
    : supabase.from("products").update(productToRow(p)).eq("id", p.id).select().single();
  return rowToProduct(unwrap(await query) as Row);
};

export const removeProduct = async (id: string): Promise<void> => {
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ---------------------------------------------------------------- Sale (delete)
export const removeSale = async (id: string): Promise<void> => {
  const { error } = await supabase.from("sales").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// -------------------------------------------------------------- Distributor
export const saveDistributor = async (d: Distributor): Promise<Distributor> => {
  const query = supabase
    .from("profiles")
    .update(distributorToRow(d))
    .eq("id", d.id)
    .select()
    .single();
  return rowToDistributor(unwrap(await query) as Row);
};

export interface ProfileContact {
  names?: string;
  surnames?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
}

// Actualiza los datos de contacto de un perfil. El propio usuario puede
// actualizar su cuenta y un administrador puede actualizar la de cualquiera
// (asesor o administrador), según las políticas de seguridad de la base.
export const updateProfileContact = async (
  id: string,
  contact: ProfileContact
): Promise<void> => {
  const row: Row = { updated_at: new Date().toISOString() };
  if (contact.names !== undefined) row.names = contact.names;
  if (contact.surnames !== undefined) row.surnames = contact.surnames;
  if (contact.email !== undefined) row.email = contact.email;
  if (contact.phone !== undefined) row.phone = contact.phone;
  if (contact.address !== undefined) row.address = contact.address;
  if (contact.city !== undefined) row.city = contact.city;
  const { error } = await supabase.from("profiles").update(row).eq("id", id);
  if (error) throw new Error(error.message);
};

// Cambia el estado de aprobación de un asesor (pending/approved/rejected/suspended).
export const setApprovalStatus = async (
  id: string,
  status: ApprovalStatus
): Promise<Distributor> => {
  const query = supabase
    .from("profiles")
    .update({ approval_status: status, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  return rowToDistributor(unwrap(await query) as Row);
};

// -------------------------------------------------------------------- Team
// Carga TODAS las cuentas (administradores y asesores) para gestionar roles.
export const loadTeamProfiles = async (): Promise<TeamMember[]> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data as Row[]) ?? []).map(rowToTeamMember);
};

// Cambia el rol de una cuenta. Al promover a administrador se marca como aprobada.
export const setProfileRole = async (id: string, role: Role): Promise<TeamMember> => {
  const patch: Row = { role, updated_at: new Date().toISOString() };
  if (role === "admin") patch.approval_status = "approved";
  const { data, error } = await supabase
    .from("profiles")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return rowToTeamMember(data as Row);
};

export interface CreateAccountPayload {
  email: string;
  password: string;
  names: string;
  surnames: string;
  role: Role;
  cedula?: string;
  phone?: string;
  city?: string;
}

// Crea una cuenta nueva (admin o asesor) mediante la función segura del backend.
export const createAccount = async (payload: CreateAccountPayload): Promise<void> => {
  const { data, error } = await supabase.functions.invoke("admin-create-account", {
    body: payload,
  });
  if (error) throw new Error(error.message);
  const serverError = (data as { error?: string } | null)?.error;
  if (serverError) throw new Error(serverError);
};

// Elimina por completo la cuenta de un asesor (usuario + perfil) mediante la
// función segura del backend.
export const deleteAccount = async (userId: string): Promise<void> => {
  const { data, error } = await supabase.functions.invoke("admin-delete-account", {
    body: { userId },
  });
  if (error) throw new Error(error.message);
  const serverError = (data as { error?: string } | null)?.error;
  if (serverError) throw new Error(serverError);
};

// Cambia la contraseña del propio usuario autenticado.
export const changeOwnPassword = async (password: string): Promise<void> => {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw new Error(error.message);
};

// Cambia la contraseña de OTRA cuenta (solo administrador) vía función segura.
export const adminSetPassword = async (userId: string, password: string): Promise<void> => {
  const { data, error } = await supabase.functions.invoke("admin-set-password", {
    body: { userId, password },
  });
  if (error) throw new Error(error.message);
  const serverError = (data as { error?: string } | null)?.error;
  if (serverError) throw new Error(serverError);
};

// ------------------------------------------------------------------ Client
export const saveClient = async (c: Client): Promise<Client> => {
  const isNew = isNewId(c.id);
  const query = isNew
    ? supabase.from("clients").insert(clientToRow(c)).select().single()
    : supabase.from("clients").update(clientToRow(c)).eq("id", c.id).select().single();
  return rowToClient(unwrap(await query) as Row);
};

export const removeClient = async (id: string): Promise<void> => {
  const { error } = await supabase.from("clients").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------------- Sale
export const saveSale = async (s: Sale): Promise<Sale> => {
  const row = saleToRow(s);
  if (s.advisorId && s.advisorId !== "admin") row.advisor_id = s.advisorId;
  const query = supabase.from("sales").insert(row).select().single();
  const saved = rowToSale(unwrap(await query) as Row);
  // La venta directa del admin se guarda con su uuid; la devolvemos como "admin"
  // para que el reparto del 25%, la ganancia y los fondos la reconozcan al instante.
  return s.advisorId === "admin" ? { ...saved, advisorId: "admin" } : saved;
};

export interface AdvisorSalePayload {
  productId: string;
  qty: number;
  source: "consignado" | "propio";
  clientId: string | null;
  clientName: string;
  pv: number;
  total: number;
  marginPct: number;
  unitCost: number;
}

// El asesor registra su venta mediante una función segura del backend que
// descuenta el stock (consignación o propio), registra la venta y la deuda.
export const advisorRegisterSale = async (p: AdvisorSalePayload): Promise<void> => {
  const { error } = await supabase.rpc("advisor_register_sale", {
    p_product_id: p.productId,
    p_qty: p.qty,
    p_source: p.source,
    p_client_id: p.clientId,
    p_client_name: p.clientName,
    p_pv: p.pv,
    p_total: p.total,
    p_margin_pct: p.marginPct,
    p_unit_cost: p.unitCost,
  });
  if (error) throw new Error(error.message);
};

// --------------------------------------------------------------- Inventory
export const saveInventory = async (i: InventoryItem): Promise<InventoryItem> => {
  const query = supabase
    .from("inventory_items")
    .upsert(inventoryToRow(i), { onConflict: "product_id" })
    .select()
    .single();
  return rowToInventory(unwrap(await query) as Row);
};

export const removeInventory = async (productId: string): Promise<void> => {
  const { error } = await supabase
    .from("inventory_items")
    .delete()
    .eq("product_id", productId);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Receivable
export const saveReceivable = async (r: Receivable): Promise<Receivable> => {
  const isNew = isNewId(r.id);
  const query = isNew
    ? supabase.from("receivables").insert(receivableToRow(r)).select().single()
    : supabase.from("receivables").update(receivableToRow(r)).eq("id", r.id).select().single();
  return rowToReceivable(unwrap(await query) as Row);
};

export const removeReceivable = async (id: string): Promise<void> => {
  const { error } = await supabase.from("receivables").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------------ Loan
export const saveLoan = async (l: Loan): Promise<Loan> => {
  const isNew = isNewId(l.id);
  const query = isNew
    ? supabase.from("loans").insert(loanToRow(l)).select().single()
    : supabase.from("loans").update(loanToRow(l)).eq("id", l.id).select().single();
  return rowToLoan(unwrap(await query) as Row);
};

export const removeLoan = async (id: string): Promise<void> => {
  const { error } = await supabase.from("loans").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// Recarga ligera (sin pantalla de carga) de inventario, ventas y movimientos.
export const loadAdvisorRuntime = async (
  advisorIds: string[] = []
): Promise<{
  inventory: InventoryItem[];
  sales: Sale[];
  advisorMovements: AdvisorMovement[];
}> => {
  const [inventory, sales, movements] = await Promise.all([
    supabase.from("inventory_items").select("*"),
    supabase.from("sales").select("*").order("date", { ascending: false }),
    supabase.from("advisor_movements").select("*").order("date", { ascending: false }),
  ]);
  return {
    inventory: unwrapList(inventory).map(rowToInventory),
    sales: normalizeSales(new Set(advisorIds), unwrapList(sales).map(rowToSale)),
    advisorMovements: unwrapList(movements).map(rowToAdvisorMovement),
  };
};

// ------------------------------------------------- Advisor movement
export const saveAdvisorMovement = async (m: AdvisorMovement): Promise<AdvisorMovement> => {
  const query = supabase
    .from("advisor_movements")
    .insert(advisorMovementToRow(m))
    .select()
    .single();
  return rowToAdvisorMovement(unwrap(await query) as Row);
};

export const removeAdvisorMovement = async (id: string): Promise<void> => {
  const { error } = await supabase.from("advisor_movements").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Expense
export const saveExpense = async (e: Expense): Promise<Expense> => {
  const isNew = isNewId(e.id);
  const query = isNew
    ? supabase.from("expenses").insert(expenseToRow(e)).select().single()
    : supabase.from("expenses").update(expenseToRow(e)).eq("id", e.id).select().single();
  return rowToExpense(unwrap(await query) as Row);
};

export const removeExpense = async (id: string): Promise<void> => {
  const { error } = await supabase.from("expenses").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Promotion
export const savePromotion = async (p: Promotion): Promise<Promotion> => {
  const isNew = isNewId(p.id);
  const query = isNew
    ? supabase.from("promotions").insert(promotionToRow(p)).select().single()
    : supabase.from("promotions").update(promotionToRow(p)).eq("id", p.id).select().single();
  return rowToPromotion(unwrap(await query) as Row);
};

export const removePromotion = async (id: string): Promise<void> => {
  const { error } = await supabase.from("promotions").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ---------------------------------------------------------- Notification
export const saveNotification = async (
  n: Omit<AppNotification, "id">
): Promise<AppNotification> => {
  const query = supabase.from("notifications").insert(notificationToRow(n)).select().single();
  return rowToNotification(unwrap(await query) as Row);
};

export const setNotificationRead = async (id: string): Promise<void> => {
  const { error } = await supabase.from("notifications").update({ read: true }).eq("id", id);
  if (error) throw new Error(error.message);
};

export const setAllNotificationsRead = async (
  audience: "admin" | "asesor"
): Promise<void> => {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .in("audience", [audience, "all"])
    .eq("read", false);
  if (error) throw new Error(error.message);
};

// -------------------------------------------------------------- Settings
export const saveSettings = async (s: Settings): Promise<void> => {
  const { error } = await supabase.from("app_settings").upsert(
    {
      id: 1,
      logistics_fee: s.logisticsFee,
      distribution: s.distribution,
      title_style: s.titleStyle,
      wallet_balances: s.walletBalances,
      logo_url: s.logoUrl,
      kit_items: s.kitItems,
      fund_movements: s.fundMovements,
      categories: s.categories ?? [],
      referral_pv_by_month: s.referralPvByMonth ?? {},
      referral_pv: 0,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" }
  );
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Import log
export const saveImportLog = async (l: ImportLog): Promise<ImportLog> => {
  const query = supabase.from("import_logs").insert(importLogToRow(l)).select().single();
  return rowToImportLog(unwrap(await query) as Row);
};

export const markImportLogUndone = async (id: string): Promise<void> => {
  const { error } = await supabase.from("import_logs").update({ undone: true }).eq("id", id);
  if (error) throw new Error(error.message);
};

export const removeImportLog = async (id: string): Promise<void> => {
  const { error } = await supabase.from("import_logs").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ----------------------------------------------------------------- Encargo
export const saveEncargo = async (e: Encargo): Promise<Encargo> => {
  const query = supabase.from("encargos").insert(encargoToRow(e)).select().single();
  return rowToEncargo(unwrap(await query) as Row);
};

export const updateEncargoStatus = async (
  id: string,
  status: EncargoStatus
): Promise<Encargo> => {
  const query = supabase.from("encargos").update({ status }).eq("id", id).select().single();
  return rowToEncargo(unwrap(await query) as Row);
};

// Marca el pedido como entregado y guarda cómo se entregó (compra o consignación)
// junto con los costos para que el admin pueda revisar gasto, ganancia y descuento.
export const updateEncargoDelivery = async (
  id: string,
  patch: {
    mode: EncargoDeliverMode;
    wallet: string | null;
    unitCostBase: number;
    unitCostAdvisor: number;
  }
): Promise<Encargo> => {
  const query = supabase
    .from("encargos")
    .update({
      status: "entregado",
      deliver_mode: patch.mode,
      deliver_wallet: patch.wallet,
      unit_cost_base: patch.unitCostBase,
      unit_cost_advisor: patch.unitCostAdvisor,
    })
    .eq("id", id)
    .select()
    .single();
  return rowToEncargo(unwrap(await query) as Row);
};

export const removeEncargo = async (id: string): Promise<void> => {
  const { error } = await supabase.from("encargos").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Restock
export const saveRestockItem = async (i: RestockItem): Promise<RestockItem> => {
  const query = supabase.from("restock_items").insert(restockItemToRow(i)).select().single();
  return rowToRestockItem(unwrap(await query) as Row);
};

export const removeRestockItem = async (id: string): Promise<void> => {
  const { error } = await supabase.from("restock_items").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------- Stock entry (PV)
export const saveStockEntry = async (e: StockEntry): Promise<StockEntry> => {
  const query = supabase.from("stock_entries").insert(stockEntryToRow(e)).select().single();
  return rowToStockEntry(unwrap(await query) as Row);
};

export const removeStockEntry = async (id: string): Promise<void> => {
  const { error } = await supabase.from("stock_entries").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// --------------------------------------------- Inventory adjustment (constancia)
export const saveInventoryAdjustment = async (
  a: InventoryAdjustment
): Promise<InventoryAdjustment> => {
  const query = supabase
    .from("inventory_adjustments")
    .insert(inventoryAdjustmentToRow(a))
    .select()
    .single();
  return rowToInventoryAdjustment(unwrap(await query) as Row);
};

export const removeInventoryAdjustment = async (id: string): Promise<void> => {
  const { error } = await supabase.from("inventory_adjustments").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ------------------------------------------------------------------ Realtime
export const subscribeNotifications = (
  onChange: (n: AppNotification) => void
): (() => void) => {
  const channel = supabase
    .channel("attd-notifications")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "notifications" },
      (payload) => onChange(rowToNotification(payload.new as Row))
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
};